define('core/views/common/HoverCard',[
    'jquery',
    'underscore',
    'backbone',

    'core/bus',
    'core/utils',
], function (
    $,
    _,
    Backbone,

    Bus,
    utils
) {
    'use strict';

    /**
     * HoverCard is a base class that cannot be instantiated on its own.
     * It includes all the functionality for revealing and hiding a
     * hovercard. How the card is rendered or updated, is handled by the
     * concrete class (see ProfileCard, ContextCard).
     *
     * HoverCard also maintains an internal list of instances keyed by
     * by concrete class name. This is because it is common for hovercards
     * (both types) to be re-used for different targets. For example, you
     * only need a single ContextCard for a thread with 1 parent comment
     * and 20 replies. The HoverCard class ensures that a single card is
     * created for this purpose, *but* can be bound to different targets.
     */

    var HoverCard = Backbone.View.extend({
        // CAREFUL! Backbone events defined in concrete classes completely
        // replace events defined here unless you explicitly copy them over.
        events: {
            'mouseenter': 'enter',
            'mouseleave': 'leave',
        },

        initialize: function () {
            this._id = _.uniqueId();

            this._rendered = false;
            this._hoverState = 'out';
            this._visible = false;
            this._enterTimeout = null;
            this._leaveTimeout = null;

            HoverCard.open = {};

            // Show profile event is mandatory for all HoverCards as user is
            // able to open other user's profile from any HoverCard at the moment.
            // Append the event to (probably) existing events object in order to preserve
            // child class events.
            this.events = this.events || {};
            this.events['click [data-action=profile]'] = 'handleShowProfile';

            this.listenTo(this, 'authenticating', this.keepOpen);
        },

        // should be called by after subclass finished with rendering
        render: function () {
            this.hide();
            $('body').append(this.el);

            return this;
        },

        target: function ($target) {
            // Add hover events to $target that open the hovercard

            // TODO: This is slow. We're creating *two* events for each
            //       element that launches the hovercard. We should probably
            //       delegate a hover event on the entire embed/<body>, and
            //       maintain active targets in a hash. It also leaks memory.
            $target.on('mouseenter', _.bind(this.enter, this, $target));
            $target.on('mouseleave', _.bind(this.leave, this));
        },

        enter: function ($target) {
            var self = this;

            // This method is both used in `target` above with a bound $target
            // argument and as an event handler so `$target` can also be the
            // jQuery event object. Normalize for this. See T8522.
            if ($target.originalEvent)
                $target = null;

            // re-assign target here as single card may listen for several mouseover events
            // from different targets and just showup near the target user just hovered.
            // So $target basically means current target.
            if ($target)
                self.$target = $target;

            if (self._leaveTimeout)
                clearTimeout(self._leaveTimeout);

            if (self._hoverState === 'in')
                return;

            self._hoverState = 'in';
            self._enterTimeout = _.delay(function () {
                if (self._hoverState === 'in')
                    self.show();

                self._enterTimeout = null;
            }, HoverCard.DELAY_ENTER);

            HoverCard.open[this.uid] = this;
        },

        leave: function () {
            var self = this;

            if (self._enterTimeout)
                clearTimeout(self._enterTimeout);

            if (self._hoverState === 'out')
                return;

            self._hoverState = 'out';

            self._leaveTimeout = _.delay(function () {
                if (self._hoverState === 'out')
                    self.hide();

                self._leaveTimeout = null;
            }, HoverCard.DELAY_LEAVE);

            if (HoverCard.open[this.uid])
                delete HoverCard.open[this.uid];
        },

        show: function () {
            var self = this;

            if (!self._rendered) {
                self._rendered = true;
                self.render();
            }
            // Element is hidden at this moment because of two possible reasons:
            // 1. It was just rendered - we render it with display:none style
            // 2. It was rendered, shown and then hidden, so now it is a time to show it again
            // So it is safe to move it to its position and show afterwards (won't have flickering)
            self.moveTo(self.$target);

            self.$el.show();

            self._visible = true;

            self.trigger('show');
        },

        // Displays current view's element near the target element.
        // By default the position of the tooltip is below the target, but
        // if there is no space at the bottom, the tooltip will be displayed
        // above the target.
        moveTo: function (target) {
            if (!target)  // if no target specified - there is no place to move to
                return;

            var posOffset = HoverCard.POSITION_OFFSET; // show tooltip with posOffset overlap on target
            var pos = target.offset();
            var el = this.$el;
            var elHeight = el.height();
            var containerPos = this.getContainerPosition();

            pos.top -= posOffset;  // show it slightly above the target

            // try to position tooltip below the target
            var cardBottom = pos.top + elHeight + containerPos.containerOffset.top;
            var containerBottom = containerPos.pageOffset + containerPos.containerHeight;

            if (cardBottom <= containerBottom)
                el.css('top', pos.top);
            else  // if there is not space in a bottom, show tooltip above the target
                el.css('top', pos.top - elHeight + posOffset * 2);

            el.css('left', pos.left + posOffset);
        },

        getContainerPosition: function () {
            return {
                pageOffset: $(window).scrollTop(),
                containerOffset: {
                    top: 0,
                    height: $(window).height(),
                },
                containerHeight: $(window).height(),
            };
        },

        // Override for lounge
        // getContainerPosition: function () {
        //     var loungePos = common.getLounge().getPosition();

        //     return {
        //         pageOffset: loungePos.pageOffset,
        //         containerOffset: loungePos.frameOffset,
        //         containerHeight: loungePos.height
        //     };
        // },

        hide: function () {
            if (this._keepOpen)
                return;

            if (this._enterTimeout)
                clearTimeout(this._enterTimeout);

            this.$el.hide();
            this._visible = false;
        },

        // Keeps the hover card open while the user is off doing something else
        // e.g., if the user clicks the "follow" button in a hover card while logged out,
        // call this function to keep the hover card open while the user logs in
        keepOpen: function () {
            this._keepOpen = true;
            this.setupKeepOpenCanceler();
        },

        setupKeepOpenCanceler: function () {
            var self = this;
            var cancel = function () {
                if (self._hoverState !== 'out')
                    return;

                self.stopListening(Bus, 'window.click', cancel);
                $('body').off('click', cancel);
                self._keepOpen = false;
                self.hide();
            };

            // When you bind to 'click' in the middle of handling a
            // click, the handler that you bind during the click gets
            // triggered by the click in which you bound the handler (!)
            // Hence the delay.
            _.delay(function () {
                // Click outside of the iframe
                self.listenTo(Bus, 'window.click', cancel);
                // Click inside the iframe
                $('body').on('click', cancel);
            }, 100);
        },

        isVisible: function () {
            return this._visible;
        },

        /**
         * This handler should only contain hover card specific actions.
         * Actual opening of profile will be handled when the event
         * propagates to the lounge click event handlers.
         */
        handleShowProfile: utils.preventDefaultHandler(function () {
            this.hide();
        }),

    }, {
        // Class properties (HoverCard.*)

        // Hash/truth table of open hovercard instances. Used to bulk exit
        // multiple hovercards (i.e. when the user mouseouts the iframe)
        open: {},

        // Instances track open card instances by concrete class name
        // {
        //    ProfileCard: { 1: true, 2: true, 3: true },
        //    ContextCard: { 8: true, 9: true }
        // }
        instances: {},

        // Time in milliseconds before mouseenter triggers show
        DELAY_ENTER: 350,

        // Time in milliseconds before mouseouts triggers reveal
        DELAY_LEAVE: 175,

        // Amount of pixels to overlap with target element position
        POSITION_OFFSET: 20,

        // Exits every open hovercard. Used in conjunction with document mouseout
        // so that we only have a single event handler that closes hovercards
        // when the user mouses out of the iframe.

        exitAll: function () {
            _.invoke(HoverCard.open, 'leave');
        },

        // A common trait of hovercards is that a single card can appear
        // in multiple contexts. Rather than re-create the hovercard
        // over and over, it's easier to create a single instance and
        // reposition it. This helper tracks concrete class instances and
        // ensures that no duplicates are made.

        create: function (id, options, className, ClassConstructor) {
            var classInstances = HoverCard.instances[className];
            if (!classInstances)
                HoverCard.instances[className] = classInstances = {};

            // TODO: Work with anonymous profiles
            var card = classInstances[id];
            if (!card) {
                card = new ClassConstructor(options);
                classInstances[id] = card;
            }

            if (options.targetElement)
                card.target(options.targetElement);

            return card;
        },
    });

    (function () {
        // Detect when mouse leaves iframe window
        // http://stackoverflow.com/questions/923299/how-can-i-detect-when-the-mouse-leaves-the-window
        $(window.document).on('mouseout', _.debounce(function (evt) {
            var from = evt.relatedTarget || evt.toElement;
            if (!from || from.nodeName === 'HTML')
                HoverCard.exitAll();
        }, 10));
    })();

    return HoverCard;
});

